import {DataTable} from "../DataTable/DataTable";
import {PermissionEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {usePermissionDataTableFilter} from "../PermissionDataTable/usePermissionDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {type ReactNode, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";

const EMPTY_ARRAY: PermissionEntity[] = []

export type BasePermissionDataTableProps = EntityDataTableProps<PermissionEntity> & {
  renderActions: (record: PermissionEntity, index: number) => ReactNode
}

export const BasePermissionDataTable = (props: BasePermissionDataTableProps) => {
  const {
    records: permissions = EMPTY_ARRAY,
    recordsPerPageOptions,
    renderActions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = usePermissionDataTableFilter({
    records: permissions
  })

  const sortConfig = useMemo(() => {
    return  {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
      "description": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.description?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<PermissionEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.permission.table.column.name", "Name"),
        accessor: 'name',
        width: 400,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "description"),
        title: t("common.entity.permission.table.description.person", "Description"),
        accessor: 'description',
        sortable: true,
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: renderActions,
      }
    ]

    return values
  }, [filterConfig, renderActions, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
